import { resolveKryspaceReadSource } from '../config/kryspaceReadSource';
import { issueTypeRefForSlug } from '../utils/issueTypeRefForSlug';
import { httpErrorDetailMessage } from '../utils/httpErrorDetail';
import { apiClient } from './apiClient';

export interface WorkspaceIssueRow {
  itemId: string;
  name: string;
  currentState: string;
  artifactTypeRef: string;
  issueSummary?: string;
  issueDescription?: string;
  issueSeverity?: string;
  discoveredAt?: string;
  issueType?: string;
}

export interface FetchWorkspaceIssuesResult {
  rows: WorkspaceIssueRow[];
  total: number;
  truncated: boolean;
  limit: number;
  offset: number;
}

export class ControlBridgeIssuesQueryError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'ControlBridgeIssuesQueryError';
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function normalizeIssueRow(raw: unknown): WorkspaceIssueRow | null {
  const row = asRecord(raw);
  if (!row) return null;
  const itemId =
    (typeof row.item_id === 'string' && row.item_id.trim()) ||
    (typeof row.itemId === 'string' && row.itemId.trim()) ||
    '';
  if (!itemId) return null;
  const name =
    (typeof row.name === 'string' && row.name.trim()) ||
    (typeof row.item_name === 'string' && row.item_name.trim()) ||
    itemId;
  const currentState =
    (typeof row.current_state === 'string' && row.current_state.trim()) ||
    (typeof row.currentState === 'string' && row.currentState.trim()) ||
    '';
  const artifactTypeRef =
    (typeof row.artifact_type_ref === 'string' && row.artifact_type_ref.trim()) ||
    (typeof row.artifactTypeRef === 'string' && row.artifactTypeRef.trim()) ||
    '';
  return {
    itemId,
    name,
    currentState,
    artifactTypeRef,
    issueSummary: optionalString(row.issue_summary ?? row.issueSummary),
    issueDescription: optionalString(row.issue_description ?? row.issueDescription),
    issueSeverity: optionalString(row.issue_severity ?? row.issueSeverity),
    discoveredAt: optionalString(row.discovered_at ?? row.discoveredAt),
    issueType: optionalString(row.issue_type ?? row.issueType),
  };
}

/** Non-terminal issue states for the product inbox (includes INITIALIZED). */
export function isInboxIssueState(state: string): boolean {
  const s = state.trim().toUpperCase();
  if (!s) return true;
  return s !== 'CLOSED' && s !== 'SUPERSEDED' && s !== 'CANCELLED' && s !== 'RESOLVED';
}

/** API max is 500; inbox loads at the max so Central filed issues are not truncated at 100. */
export const WORKSPACE_ISSUES_QUERY_LIMIT = 500;

export async function fetchWorkspaceIssues(
  workspaceSlug: string,
  opts?: { limit?: number; offset?: number }
): Promise<FetchWorkspaceIssuesResult> {
  const slug = workspaceSlug.trim();
  if (!slug) {
    throw new ControlBridgeIssuesQueryError('Workspace slug is required', 'NOT_CONFIGURED');
  }
  const artifactTypeRef = issueTypeRefForSlug(slug);
  const source = resolveKryspaceReadSource({ workspaceSlug: slug });
  const predicate = JSON.stringify({ artifact_type_ref: artifactTypeRef });
  const limit = Math.max(1, Math.min(opts?.limit ?? WORKSPACE_ISSUES_QUERY_LIMIT, 500));
  const offset = Math.max(0, opts?.offset ?? 0);
  const url =
    `/kryspace/workspace/${encodeURIComponent(slug)}/query` +
    `?source=${encodeURIComponent(source)}` +
    `&predicate=${encodeURIComponent(predicate)}` +
    `&limit=${limit}` +
    `&offset=${offset}` +
    `&include_superseded=false`;

  try {
    const { data } = await apiClient.get<unknown>(url);
    const body = asRecord(data) ?? {};
    const items = Array.isArray(body.items) ? body.items : [];
    const rows = items
      .map(normalizeIssueRow)
      .filter((row): row is WorkspaceIssueRow => row != null)
      .filter((row) => isInboxIssueState(row.currentState));
    const total = typeof body.total === 'number' ? body.total : rows.length;
    const truncated = body.truncated === true || total > offset + limit;
    return { rows, total, truncated, limit, offset };
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
    const detail = axiosErr.response?.data?.detail;
    const message = httpErrorDetailMessage(
      detail,
      axiosErr.message || 'Failed to load workspace issues'
    );
    throw new ControlBridgeIssuesQueryError(message, 'REQUEST_FAILED', axiosErr.response?.status);
  }
}
